import { computed, readonly, ref, toRaw, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { TriggerActionForm, TriggerActionActionTypeWithGroup, TriggerActionActionType } from '@/api/openapi';
import { TriggerActionFormApiService } from '@/api/user/resources/trigger_action_form';
import { TriggerActionTypeApiService } from '@/api/user/resources/trigger_action_type';
import { TriggerActionBodyExt } from '@/components/organisms/user/trigger/types';
import { useWait } from '@/composable/vue-wait';

const useTriggerAction = (needsInitialFetch = () => true) => {
  const i18n = useI18n();

  const actionTypes = ref<TriggerActionActionTypeWithGroup[]>([]);

  const groupedActionTypes = computed(() => {
    return actionTypes.value.reduce((acc, actionType) => {
      let group = acc.find((group) => group.key === actionType.group);
      if (!group) {
        group = {
          key: actionType.group,
          label: i18n.t(`trigger.actionGroups.${actionType.group}`),
          options: [],
        };
        acc.push(group);
      }
      group.options.push({
        value: actionType.actionType,
        name: i18n.t(`trigger.actionTypes.${actionType.actionType}`),
      });
      return acc;
    }, []);
  });

  const fetchActionTypes = async () => {
    const api = new TriggerActionTypeApiService();
    const { data } = await api.getTriggerActionTypes();
    actionTypes.value = data.triggerActionTypes;
  };

  const makeInitialTriggerAction = (triggerAction: TriggerActionBodyExt): TriggerActionBodyExt => {
    const { actionDetail, ...rest } = structuredClone(toRaw(triggerAction) || {}) as Partial<TriggerActionBodyExt>;
    return {
      approveUserId: null,
      actionType: null,
      actionDetail: actionDetail || {},
      sortOrder: null,
      ...rest,
    };
  };

  if (needsInitialFetch()) {
    fetchActionTypes();
  }

  return {
    groupedActionTypes: readonly(groupedActionTypes),
    makeInitialTriggerAction,
  };
};

const useTriggerActionForm = () => {
  const { doActionWithWait, wait } = useWait();

  const forms = ref<TriggerActionForm[]>([]);
  const fetchForms = async (actionType: TriggerActionActionType) => {
    await doActionWithWait('fetchTriggerActionFormsWait', async () => {
      const api = new TriggerActionFormApiService();
      const { data } = await api.getTriggerActionForms({
        request: {
          actionType,
        },
      });
      forms.value = data.triggerActionForms;
    });
  };

  const loading = computed(() => {
    return wait.is('fetchTriggerActionFormsWait');
  });

  return {
    loading: readonly(loading),
    forms,
    fetchForms,
  };
};

// カードの常時表示用に、actionType ごとの forms をページ単位でキャッシュする。
// actionTypesSource が返す型集合を監視し、未取得の型だけを取得する。
const useTriggerActionFormsCache = (
  actionTypesSource: () => (TriggerActionActionType | null | undefined)[],
) => {
  const cache = ref<Partial<Record<TriggerActionActionType, TriggerActionForm[]>>>({});
  const inflight = new Map<TriggerActionActionType, Promise<void>>();
  const inflightCount = ref(0);

  const ensure = (actionType: TriggerActionActionType) => {
    if (cache.value[actionType] || inflight.has(actionType)) return;
    const api = new TriggerActionFormApiService();
    inflightCount.value += 1;
    const promise = api
      .getTriggerActionForms({ request: { actionType } })
      .then(({ data }) => {
        cache.value[actionType] = data.triggerActionForms;
      })
      .catch(() => {
        // 失敗時はキャッシュしない。次回の変更検知で再取得される。
      })
      .finally(() => {
        inflight.delete(actionType);
        inflightCount.value -= 1;
      });
    inflight.set(actionType, promise);
  };

  watch(
    actionTypesSource,
    (actionTypes) => {
      actionTypes.forEach((actionType) => {
        if (actionType) ensure(actionType);
      });
    },
    { immediate: true, deep: true },
  );

  const formsFor = (actionType: TriggerActionActionType | null | undefined): TriggerActionForm[] => {
    if (!actionType) return [];
    return cache.value[actionType] ?? [];
  };

  const loading = computed(() => inflightCount.value > 0);

  return { formsFor, loading: readonly(loading) };
};

export {
  useTriggerAction,
  useTriggerActionForm,
  useTriggerActionFormsCache,
};
